/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/QaNewChecklistDraftCards.tsx
 * FILE_ID: JOBHUB-QA-NEWCL-DRAFT-CARDS-001
 * PURPOSE: Card layout for new-checklist draft rows: the phone sheet's default.
 * LANDMINE: same fields and aria-labels as QaNewChecklistDraftTable, so both
 *      layouts edit one draft and the toggle never loses a typed value.
 */
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { EditableCell } from '../../../qa/EditableCell'
import { QaProductSearchField } from '../../../qa/QaProductSearchField'
import type { JobHubHttpClient } from '../../../jobHubDataAdapter'
import { Field } from '../phone/build/QaPhoneBuildCard'
import type { QaDraftRow } from './useQaNewChecklistDraft'

const iconButton =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground disabled:opacity-40'

export function QaNewChecklistDraftCards({
  rows,
  onPatch,
  onMove,
  onRemove,
  client,
}: {
  rows: QaDraftRow[]
  onPatch: (key: string, fields: Partial<QaDraftRow>) => void
  onMove: (index: number, direction: -1 | 1) => void
  onRemove: (key: string) => void
  client?: JobHubHttpClient
}) {
  return (
    <ul className="space-y-3">
      {rows.map((row, index) => (
        <li key={row.key} data-draft-row={row.key} className="space-y-2 rounded-xl border border-border bg-card p-2">
          <div className="flex items-center gap-1">
            <span className="min-w-0 flex-1 px-2 text-xs font-semibold text-foreground">Check {index + 1}</span>
            <button type="button" aria-label="Move draft row up" disabled={index === 0} onClick={() => onMove(index, -1)} className={iconButton}>
              <ChevronUp className="h-4 w-4" aria-hidden />
            </button>
            <button type="button" aria-label="Move draft row down" disabled={index === rows.length - 1} onClick={() => onMove(index, 1)} className={iconButton}>
              <ChevronDown className="h-4 w-4" aria-hidden />
            </button>
            <button type="button" aria-label="Remove draft row" onClick={() => onRemove(row.key)} className={iconButton}>
              <X className="h-4 w-4 text-destructive" aria-hidden />
            </button>
          </div>
          <Field label="Check">
            <EditableCell
              value={row.title}
              singleLine
              allowEmpty
              ariaLabel={`Draft row ${index + 1} title`}
              placeholder="Check name"
              onCommit={(next) => onPatch(row.key, { title: next })}
            />
          </Field>
          <Field label="Product">
            <QaProductSearchField
              value={row.product}
              client={client}
              ariaLabel={`Draft row ${index + 1} product`}
              onCommit={(next) => onPatch(row.key, { product: next })}
            />
          </Field>
          <Field label="Requirement">
            <EditableCell
              value={row.requirement_text}
              ariaLabel={`Draft row ${index + 1} requirement`}
              placeholder="What to verify"
              onCommit={(next) => onPatch(row.key, { requirement_text: next })}
            />
          </Field>
          <Field label="Acceptance criteria">
            <EditableCell
              value={row.acceptance_criteria}
              ariaLabel={`Draft row ${index + 1} acceptance criteria`}
              placeholder="Pass condition"
              onCommit={(next) => onPatch(row.key, { acceptance_criteria: next })}
            />
          </Field>
          <label className="flex min-h-[44px] items-center gap-2 px-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={row.has_hold_point}
              aria-label={`Hold point for draft row ${index + 1}`}
              onChange={(event) => onPatch(row.key, { has_hold_point: event.target.checked })}
              className="h-4 w-4 rounded border-border text-amber-600 focus:ring-amber-500"
            />
            Hold point
          </label>
        </li>
      ))}
    </ul>
  )
}
